import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import emendaService, { resolveEmendaStatus, type EmendaDTO } from '../services/emendaService';
import institutionService, { type InstitutionDTO } from '../services/institutionService';
import councilorService, { type CouncilorDTO } from '../services/councilorService';
import tipoEmendaService, { type TipoEmendaDTO } from '../services/tipoEmendaService';
import funcaoOrcamentariaService, { type FuncaoOrcamentariaDTO } from '../services/funcaoOrcamentariaService';
import funcionalidadesService, { type FuncionalidadeDTO } from '../services/funcionalidadesService';
import parlamentarLimitesService from '../services/parlamentarLimitesService';
import { formatCurrency, parseCurrency, toUpperText } from '../utils/formatters';
import api from '../services/api';
import Toast from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import ReservaFuncionalidadesPanel, { type FuncionalidadeResumo } from '../components/ReservaFuncionalidadesPanel';
import DotacaoOrcamentariaCombobox from '../components/DotacaoOrcamentariaCombobox';
import { UI_THEME } from '../ui/theme';

interface DotacaoOrcamentaria {
  id: number;
  codigoReduzido: string;
  dotacao: string;
  descricao?: string;
}

interface ParlamentarLimiteDTO {
  id: number;
  parlamentarId: string;
  ano: number;
  valorAnual: number;
}


interface DotacaoItem {
  dotacaoOrcamentariaId?: number;
  dotacaoOrcamentariaTexto?: string;
}

interface EmendaForm {
  id?: string;
  councilorId: string;
  officialCode: string;
  date: string;
  value: string;
  classification: string;
  esfera: string;
  existeConvenio: boolean;
  numeroConvenio: string;
  anoConvenio?: number;
  category: string;
  status: string;
  institutionId: string;
  signedLink: string;
  description: string;
  objectDetail: string;
  numeroEmenda?: number;
  exercicio?: number;
  justificativa?: string;
  previsaoConclusao?: string;
  tipoTransferencia: string;
  dotacaoOrcamentariaId?: number;
  dotacaoOrcamentariaTexto?: string;
  dotacoes: DotacaoItem[];
  funcaoCodigo?: string;
  // New field: classificação — Investimento ou Custeio
  classificacao?: string;
}

const normalizeRuleText = (value?: string | null) =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toUpperCase();

const inferRuleCategory = (emenda: Partial<EmendaDTO>): 'SAUDE' | 'EDUCACAO' | null => {
  const category = normalizeRuleText(emenda.category);
  const funcaoCodigo = normalizeRuleText((emenda as any).funcaoCodigo);

  if (category === 'SAUDE' || category === 'SAUDE ') return 'SAUDE';
  if (category === 'EDUCACAO') return 'EDUCACAO';
  if (funcaoCodigo === '10') return 'SAUDE';
  if (funcaoCodigo === '12') return 'EDUCACAO';
  return null;
};

const isEmendaAtivaParaReserva = (emenda?: Pick<EmendaDTO, 'status' | 'statusCicloVida'> | null) => {
  const status = normalizeRuleText(resolveEmendaStatus(emenda));
  if (!status) return true;
  if (status.includes('REPROV')) return false;
  return !status.startsWith('DEVOLV');
};

const emendaMatchesRule = (
  emenda: Partial<EmendaDTO>,
  regra: Pick<FuncionalidadeDTO, 'chave' | 'descricao' | 'categoria'>,
  categoryRuleCount?: Record<'SAUDE' | 'EDUCACAO', number>
) => {
  const categoriaInferida = inferRuleCategory(emenda);
  if (!categoriaInferida || categoriaInferida !== regra.categoria) {
    return false;
  }

  const regraChave = normalizeRuleText(regra.chave);
  const funcaoCodigo = normalizeRuleText((emenda as any).funcaoCodigo);
  const funcionalidadeChave = normalizeRuleText((emenda as any).funcionalidadeChave);
  const category = normalizeRuleText(emenda.category);
  const descricao = normalizeRuleText(regra.descricao);

  if (funcaoCodigo && funcaoCodigo === regraChave) return true;
  if (funcionalidadeChave && funcionalidadeChave === regraChave) return true;
  if (!!category && (category === regraChave || category === descricao)) return true;
  return (categoryRuleCount?.[regra.categoria] || 0) === 1;
};

const formatPercent = (value: number) => `${value.toFixed(2)}%`;

const CadastroEmendaPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const emendaId = searchParams.get('id');
  const isVereador = String(user?.role || '').toUpperCase() === 'VEREADOR';
  const vereadorParlamentarId = user?.parlamentarId || '';
  const instituicaoIdParam = searchParams.get('instituicaoId'); // Pega instituição da URL
  const isEditMode = !!emendaId;
  const isInstituicaoLocked = !!instituicaoIdParam; // Bloqueia se veio da URL
  const autoGenerateOfficialCode = !isEditMode;

  const [form, setForm] = useState<EmendaForm>({
    councilorId: '',
    officialCode: '',
    date: new Date().toISOString().split('T')[0],
    value: 'R$ 0,00',
    classification: '',
    esfera: 'Municipal',
    existeConvenio: false,
    numeroConvenio: '',
    anoConvenio: undefined,
    category: '',
    status: 'Recebido',
    institutionId: instituicaoIdParam || '', // Preenche com o parâmetro da URL se existir
    signedLink: '',
    description: '',
    objectDetail: '',
    numeroEmenda: undefined,
    exercicio: new Date().getFullYear(),
    justificativa: '',
    previsaoConclusao: '',
    tipoTransferencia: 'Direta',
    dotacaoOrcamentariaId: undefined,
    dotacaoOrcamentariaTexto: '',
    dotacoes: [],
    funcaoCodigo: '',
    classificacao: '',
  });

  // Only allow editing when creating new emenda or when simplified status is Recebido or Iniciado
  const editableStatuses = ['recebido', 'iniciado'];
  const canEdit = !isEditMode || editableStatuses.includes((form.status || '').toLowerCase());

  const [institutions, setInstitutions] = useState<InstitutionDTO[]>([]);
  const [councilors, setCouncilors] = useState<CouncilorDTO[]>([]);
  const [tiposEmenda, setTiposEmenda] = useState<TipoEmendaDTO[]>([]);
  const [funcoesOrcamentarias, setFuncoesOrcamentarias] = useState<FuncaoOrcamentariaDTO[]>([]);
  const [funcionalidades, setFuncionalidades] = useState<FuncionalidadeDTO[]>([]);
  const [emendasDetalhadas, setEmendasDetalhadas] = useState<EmendaDTO[]>([]);
  const [limitesParlamentar, setLimitesParlamentar] = useState<ParlamentarLimiteDTO[]>([]);
  const [loadingReserva, setLoadingReserva] = useState(false);
  const [dotacoesAll, setDotacoesAll] = useState<DotacaoOrcamentaria[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const isSaas = UI_THEME === 'saas';
  const accentTextClass = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';
  const headerClass = isSaas
    ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/70 to-indigo-50 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
    : 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800';
  const panelClass = 'rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/80';
  const labelClass = 'mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200';
  const helperTextClass = 'mt-1 text-xs text-slate-500 dark:text-slate-400';
  const inputClass = 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500 dark:disabled:bg-slate-800 dark:disabled:text-slate-500';
  const dropdownClass = 'absolute left-0 right-0 z-40 mt-1 max-h-48 overflow-auto rounded-lg border border-slate-200 bg-white text-sm shadow-lg dark:border-slate-700 dark:bg-slate-900';
  const dropdownItemClass = 'cursor-pointer px-3 py-2 text-slate-700 hover:bg-indigo-50 dark:text-slate-200 dark:hover:bg-slate-800';
  const primaryButtonClass = isSaas
    ? 'rounded-lg bg-teal-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60'
    : 'rounded-lg bg-blue-700 px-5 py-2 text-sm font-semibold text-white transition hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60';
  const secondaryButtonClass = 'rounded-lg border border-slate-300 bg-white px-5 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';

  // Search states for autocomplete
  const [institutionSearch, setInstitutionSearch] = useState('');
  const [councilorSearch, setCouncilorSearch] = useState('');
  // Combobox state for Parlamentar
  const [councilorOpen, setCouncilorOpen] = useState(false);
  const councilorRef = useRef<HTMLDivElement | null>(null);
  // Combobox state for Instituição
  const [institutionOpen, setInstitutionOpen] = useState(false);
  const institutionRef = useRef<HTMLDivElement | null>(null);

  // Close institution dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (institutionRef.current && !institutionRef.current.contains(e.target as Node)) {
        setInstitutionOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Keep the search input synced with a selected institution when available
  useEffect(() => {
    if (form.institutionId && institutions.length > 0) {
      const inst = institutions.find(i => i.institutionId === form.institutionId);
      if (inst && !institutionSearch) {
        setInstitutionSearch(inst.razaoSocial || (inst as any).name || '');
      }
    }
  }, [form.institutionId, institutions]);

  // Close councilor dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (councilorRef.current && !councilorRef.current.contains(e.target as Node)) {
        setCouncilorOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Keep councilorSearch synced with selected councilor when available
  useEffect(() => {
    if (form.councilorId && councilors.length > 0) {
      const c = councilors.find(x => x.councilorId === form.councilorId);
      if (c && !councilorSearch) setCouncilorSearch(c.fullName || '');
    }
  }, [form.councilorId, councilors]);

  useEffect(() => {
    if (!isVereador || !vereadorParlamentarId || isEditMode) return;
    setForm((prev) => (
      prev.councilorId === vereadorParlamentarId
        ? prev
        : { ...prev, councilorId: vereadorParlamentarId }
    ));
  }, [isVereador, vereadorParlamentarId, isEditMode]);

  useEffect(() => {
    if (!isVereador || !vereadorParlamentarId || councilors.length === 0) return;
    const councilor = councilors.find((item) => item.councilorId === vereadorParlamentarId);
    if (!councilor) return;
    setCouncilorSearch(
      `${councilor.fullName || ''}${councilor.politicalParty ? ` (${councilor.politicalParty})` : ''}`.trim()
    );
    setCouncilorOpen(false);
  }, [isVereador, vereadorParlamentarId, councilors]);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (emendaId) {
      loadEmenda(emendaId);
    }
  }, [emendaId]);

  useEffect(() => {
    const parlamentarId = form.councilorId?.trim();
    const exercicio = form.exercicio ?? new Date().getFullYear();
    if (!parlamentarId) {
      setLimitesParlamentar([]);
      return;
    }

    let ignore = false;
    const loadReservaData = async () => {
      setLoadingReserva(true);
      try {
        const [limitesRes, emendasRes] = await Promise.all([
          parlamentarLimitesService.listByParlamentar(parlamentarId),
          emendaService.listWithDetails(),
        ]);

        if (ignore) return;
        setLimitesParlamentar(Array.isArray(limitesRes.data) ? limitesRes.data : []);
        setEmendasDetalhadas(Array.isArray(emendasRes) ? emendasRes : []);
      } catch (err) {
        if (!ignore) {
          console.error('Erro ao carregar reserva por funcionalidade:', err);
        }
      } finally {
        if (!ignore) {
          setLoadingReserva(false);
        }
      }
    };

    loadReservaData();
    return () => {
      ignore = true;
    };
  }, [form.councilorId, form.exercicio]);

  // Emenda de um exercício só olha as dotações daquele ano, na área (função) da emenda.
  // Sem função escolhida, mostra todas as áreas do ano.
  useEffect(() => {
    let ignore = false;
    const params = new URLSearchParams({ limit: '500' });
    if (form.exercicio) params.set('exercicio', String(form.exercicio));
    if (form.funcaoCodigo) params.set('funcaoCodigo', form.funcaoCodigo);
    api.get<DotacaoOrcamentaria[]>(`/dotacoes-orcamentarias/search?${params.toString()}`)
      .then((r) => { if (!ignore) setDotacoesAll(r.data); })
      .catch(() => { if (!ignore) setDotacoesAll([]); });
    return () => { ignore = true; };
  }, [form.exercicio, form.funcaoCodigo]);

  const loadInitialData = async () => {
    try {
      const [instData, councData, tiposData, funcoesData, funcionalidadesRes] = await Promise.all([
        institutionService.list(),
        councilorService.list(),
        tipoEmendaService.list(),
        funcaoOrcamentariaService.list(),
        funcionalidadesService.listAll().then((response) => response.data).catch(() => [] as FuncionalidadeDTO[]),
      ]);
      setInstitutions(instData);
      setCouncilors(councData);
      // Deduplicate tiposEmenda by codigo (some backends may return duplicates)
      try {
        const tiposSeen = new Map<string, TipoEmendaDTO>();
        for (const t of tiposData || []) {
          if (!tiposSeen.has(t.codigo)) tiposSeen.set(t.codigo, t);
        }
        setTiposEmenda(Array.from(tiposSeen.values()));
      } catch (e) {
        setTiposEmenda(tiposData);
      }
      setFuncoesOrcamentarias(funcoesData);
      setFuncionalidades((funcionalidadesRes || []).filter((item) => item.ativo));
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Erro ao carregar dados iniciais');
    }
  };

  const loadEmenda = async (id: string) => {
    setLoading(true);
    try {
      // Use the detailed DTO which contains normalized classificacao code
      const data = await emendaService.getByIdWithDetails(id);
      setForm({
        id: data.id,
        councilorId: data.councilorId || '',
        officialCode: data.officialCode || '',
        date: data.date || new Date().toISOString().split('T')[0],
        value: data.value ? formatCurrency(data.value) : 'R$ 0,00',
        classification: data.classification || '',
        esfera: data.esfera || 'Municipal',
        existeConvenio: data.existeConvenio || false,
        numeroConvenio: data.numeroConvenio || '',
        anoConvenio: data.anoConvenio,
        category: data.category || '',
        status: data.status || resolveEmendaStatus(data),
        institutionId: data.institutionId || '',
        signedLink: data.signedLink || '',
        description: data.description || '',
        objectDetail: data.objectDetail || '',
        numeroEmenda: data.numeroEmenda,
        exercicio: data.exercicio,
        justificativa: data.justificativa || '',
        previsaoConclusao: data.previsaoConclusao || '',
        tipoTransferencia: data.tipoTransferencia || 'Direta',
        dotacaoOrcamentariaId: data.dotacaoOrcamentariaId,
        dotacaoOrcamentariaTexto: data.dotacaoOrcamentariaTexto || '',
        dotacoes: data.dotacoes && data.dotacoes.length > 0
          ? data.dotacoes
          : (data.dotacaoOrcamentariaId || data.dotacaoOrcamentariaTexto
              ? [{ dotacaoOrcamentariaId: data.dotacaoOrcamentariaId, dotacaoOrcamentariaTexto: data.dotacaoOrcamentariaTexto || '' }]
              : []),
        funcaoCodigo: data.funcaoCodigo || '',
        // Ensure classificacao is set as a string matching the select values ('1'/'2')
        classificacao: data.classificacao != null ? String(data.classificacao) : '',
      });
    } catch (err) {
      console.error('Erro ao carregar emenda:', err);
      setError('Erro ao carregar emenda');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof EmendaForm, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Ano corrente e o seguinte (a emenda do exercício que vem é cadastrada antes de o ano virar), mais o
  // exercício já gravado quando a emenda é antiga.
  const exercicioOptions = Array.from(new Set([
    new Date().getFullYear(),
    new Date().getFullYear() + 1,
    ...(form.exercicio ? [form.exercicio] : []),
  ])).sort((a, b) => a - b);

  // Dotação é de um exercício: ao trocar o ano, as já escolhidas deixam de valer e saem da lista.
  const handleExercicioChange = (exercicio: number) => {
    setForm((prev) => ({ ...prev, exercicio, dotacoes: [] }));
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/[^\d]/g, '');
    const numValue = parseInt(rawValue || '0', 10);
    const formatted = formatCurrency(numValue / 100);
    handleChange('value', formatted);
  };

  const reservaFuncionalidades = useMemo(() => {
    const exercicio = form.exercicio ?? new Date().getFullYear();
    const limiteAnual = Number(
      limitesParlamentar.find((item) => item.ano === exercicio)?.valorAnual ?? 0
    );

    const emendasAtivas = emendasDetalhadas.filter((item) => {
      if ((item.councilorId || '') !== form.councilorId) return false;
      if ((item.exercicio ?? exercicio) !== exercicio) return false;
      if (isEditMode && form.id && item.id === form.id) return false;
      return isEmendaAtivaParaReserva(item);
    });

    const totalAplicadoAtual = emendasAtivas.reduce((sum, item) => sum + Number(item.value || 0), 0);
    const funcionalidadesAtivas = funcionalidades.filter((item) => item.ativo);
    const categoryRuleCount = funcionalidadesAtivas.reduce<Record<'SAUDE' | 'EDUCACAO', number>>(
      (acc, item) => {
        acc[item.categoria] = (acc[item.categoria] || 0) + 1;
        return acc;
      },
      { SAUDE: 0, EDUCACAO: 0 }
    );

    const resumos: FuncionalidadeResumo[] = funcionalidadesAtivas.map((item) => {
      const percentual = Number(item.percentual || 0);
      const metaMinima = (limiteAnual * percentual) / 100;
      const aplicado = emendasAtivas
        .filter((emenda) => emendaMatchesRule(emenda, item, categoryRuleCount))
        .reduce((sum, emenda) => sum + Number(emenda.value || 0), 0);
      const faltante = Math.max(metaMinima - aplicado, 0);
      return {
        id: item.id,
        chave: item.chave,
        descricao: item.descricao || item.chave,
        categoria: item.categoria,
        percentual,
        metaMinima,
        aplicado,
        faltante,
        disponivelAgora: 0,
        matchesCurrentSelection: emendaMatchesRule({ funcaoCodigo: form.funcaoCodigo, category: form.category }, item, categoryRuleCount),
      };
    });

    resumos.forEach((item) => {
      const reservaOutras = resumos
        .filter((other) => other.id !== item.id)
        .reduce((sum, other) => sum + other.faltante, 0);
      item.disponivelAgora = Math.max(limiteAnual - totalAplicadoAtual - reservaOutras, 0);
    });

    const totalPercentual = resumos.reduce((sum, item) => sum + item.percentual, 0);
    const percentualLivre = Math.max(100 - totalPercentual, 0);
    const metaDemais = Math.max(
      limiteAnual - resumos.reduce((sum, item) => sum + item.metaMinima, 0),
      0
    );
    const aplicadoDemais = emendasAtivas
      .filter((emenda) => !resumos.some((item) => emendaMatchesRule(emenda, item, categoryRuleCount)))
      .reduce((sum, emenda) => sum + Number(emenda.value || 0), 0);
    const disponivelDemais = Math.max(metaDemais - aplicadoDemais, 0);

    const selectedResumo = resumos.find((item) => item.matchesCurrentSelection) || null;
    const disponivelParaSelecao = selectedResumo ? selectedResumo.disponivelAgora : disponivelDemais;
    const rotuloSelecao = selectedResumo ? selectedResumo.descricao : 'Demais funções';

    return {
      limiteAnual,
      totalAplicadoAtual,
      resumos,
      percentualLivre,
      metaDemais,
      aplicadoDemais,
      disponivelDemais,
      disponivelParaSelecao,
      rotuloSelecao,
    };
  }, [emendasDetalhadas, funcionalidades, form.category, form.councilorId, form.exercicio, form.funcaoCodigo, form.id, isEditMode, limitesParlamentar]);

  const validateForm = (): string[] => {
    const errors: string[] = [];

    if (!form.councilorId) errors.push('Parlamentar é obrigatório');
    if (!form.institutionId) errors.push('Instituição é obrigatória');
    if (!autoGenerateOfficialCode && !form.officialCode) errors.push('Código Oficial é obrigatório');
    if (!form.date) errors.push('Data é obrigatória');
    const valorInformado = parseCurrency(form.value);
    if (!form.value || valorInformado <= 0) errors.push('Valor deve ser maior que zero');
    if (form.councilorId && loadingReserva) {
      errors.push('Aguarde o carregamento da disponibilidade por funcionalidade');
    } else if (form.councilorId && valorInformado > 0 && valorInformado > reservaFuncionalidades.disponivelParaSelecao) {
      errors.push(`Valor informado ultrapassa o disponivel para ${reservaFuncionalidades.rotuloSelecao}`);
    }
    if (!form.classification) errors.push('Tipo de Emenda é obrigatório');
    else if (!tiposEmenda.some(t => t.codigo === form.classification)) errors.push('Tipo de Emenda inválido. Selecione um valor válido.');
    if (!form.description) errors.push('Descrição é obrigatória');

    // Justificativa é opcional, mas se preenchida deve ter no mínimo 20 caracteres
    if (form.justificativa && form.justificativa.trim().length > 0 && form.justificativa.trim().length < 20) {
      errors.push('Justificativa deve ter no mínimo 20 caracteres (ou deixe vazio)');
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join('; '));
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        ...form,
        officialCode: autoGenerateOfficialCode ? '' : form.officialCode,
        value: parseCurrency(form.value),
        dotacoes: (form.dotacoes || []).filter(d => d.dotacaoOrcamentariaId || d.dotacaoOrcamentariaTexto),
      };

      console.log('📤 [CadastroEmenda] Sending payload to backend:', payload);
      console.log('📋 [CadastroEmenda] Payload details:', {
        councilorId: payload.councilorId,
        institutionId: payload.institutionId,
        officialCode: payload.officialCode,
        value: payload.value,
        classification: payload.classification,
        esfera: payload.esfera,
        existeConvenio: payload.existeConvenio,
      });

      if (isEditMode && form.id) {
        console.log('🔄 [CadastroEmenda] Updating emenda with id:', form.id);
        // Use the returned updated emenda to refresh the form locally without toggling the global `loading` state
        const updated = await emendaService.update(form.id, payload);
        setSuccess('Emenda atualizada com sucesso!');

        // A secretaria de destino compativel e derivada da dotacao da emenda no backend.
        // Sem invalidar, o painel de admissibilidade continua servindo a lista da dotacao
        // anterior (staleTime de 5 min) e so corrige apos um F5.
        queryClient.invalidateQueries({ queryKey: ['secretarias-municipais', form.id] });

        // Atualiza o formulário local com os dados retornados (evita o flash de "Carregando...")
        setForm((prev) => ({
          ...prev,
          id: updated.id,
          councilorId: updated.councilorId || prev.councilorId,
          officialCode: updated.officialCode || prev.officialCode,
          date: updated.date || prev.date,
          value: updated.value !== undefined ? formatCurrency(updated.value) : prev.value,
          classification: updated.classification || prev.classificacao,
          esfera: updated.esfera || prev.esfera,
          existeConvenio: updated.existeConvenio ?? prev.existeConvenio,
          numeroConvenio: updated.numeroConvenio || prev.numeroConvenio,
          anoConvenio: updated.anoConvenio ?? prev.anoConvenio,
          category: updated.category || prev.category,
          status: updated.status || prev.status,
          institutionId: updated.institutionId || prev.institutionId,
          signedLink: updated.signedLink || prev.signedLink,
          description: updated.description || prev.description,
          objectDetail: updated.objectDetail || prev.objectDetail,
          numeroEmenda: updated.numeroEmenda ?? prev.numeroEmenda,
          exercicio: updated.exercicio ?? prev.exercicio,
          justificativa: updated.justificativa || prev.justificativa,
          previsaoConclusao: updated.previsaoConclusao || prev.previsaoConclusao,
          tipoTransferencia: updated.tipoTransferencia || prev.tipoTransferencia,
          dotacaoOrcamentariaId: updated.dotacaoOrcamentariaId ?? prev.dotacaoOrcamentariaId,
          dotacaoOrcamentariaTexto: updated.dotacaoOrcamentariaTexto ?? prev.dotacaoOrcamentariaTexto,
          dotacoes: updated.dotacoes ?? prev.dotacoes,
          funcaoCodigo: updated.funcaoCodigo || prev.funcaoCodigo,
          // Ensure classificacao on the form is a string ('1' or '2')
          classificacao: updated.classificacao != null ? String(updated.classificacao) : prev.classificacao,
        }));

        // Voltar para a visualização da emenda após salvar.
        // Usa form.id (sempre presente em edição) com fallback no retorno do backend.
        const detalheId = form.id || updated.id;
        if (detalheId) {
          setTimeout(() => {
            navigate(`/dashboard/emendas/${detalheId}`);
          }, 700);
        }
      } else {
        console.log('➕ [CadastroEmenda] Creating new emenda');
        const created = await emendaService.create(payload);
        setSuccess(`Emenda criada com sucesso! ID gerado: ${created.officialCode || created.id}`);

        // Ao criar, abre a emenda gerada para exibir imediatamente o código oficial retornado pelo backend
        setTimeout(() => {
          navigate(`/dashboard/emendas/${created.id}`);
        }, 1200);
      }
    } catch (err: any) {
      console.error('❌ [CadastroEmenda] Error saving emenda:', err);
      console.error('❌ [CadastroEmenda] Error response:', err?.response);
      console.error('❌ [CadastroEmenda] Error data:', err?.response?.data);

      const errorMessage = err?.response?.data?.error || err?.message || 'Erro ao salvar emenda';
      console.error('❌ [CadastroEmenda] Final error message:', errorMessage);
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const filteredInstitutions = institutions.filter((inst) =>
    inst.razaoSocial?.toLowerCase().includes(institutionSearch.toLowerCase())
  );

  const filteredCouncilors = councilors.filter((c) =>
    c.fullName?.toLowerCase().includes(councilorSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="w-full space-y-6">
        <p className="text-sm text-slate-500 dark:text-slate-400">Carregando...</p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      <header className={`rounded-2xl border p-5 ${headerClass}`}>
        <p className={`text-xs font-semibold uppercase tracking-[0.22em] ${accentTextClass}`}>
          Gestão de emendas
        </p>
        <div className="mt-2 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-950 dark:text-slate-50">
              {isEditMode ? 'Editar Emenda' : 'Nova Emenda'}
            </h1>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              Preencha os dados da emenda. Campos marcados com * são obrigatórios.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs font-semibold">
            <span className="rounded-full border border-slate-200 bg-white/80 px-3 py-1 text-slate-700 dark:border-slate-700 dark:bg-slate-950/70 dark:text-slate-200">
              Etapa: {form.status}
            </span>
            <span className="rounded-full border border-slate-200 bg-white/80 px-3 py-1 text-slate-700 dark:border-slate-700 dark:bg-slate-950/70 dark:text-slate-200">
              Execução: {form.tipoTransferencia}
            </span>
          </div>
        </div>
      </header>

      {/* Toasts para erro/sucesso (posicionados no canto superior direito) */}
      {error && (
        <Toast type="error" message={error} onClose={() => setError('')} />
      )}

      {success && (
        <Toast type="success" message={success} onClose={() => setSuccess('')} />
      )}

      {/* If editing is locked because emenda is past 'Recebido', show notice and disable form */}
      {!canEdit && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
          Esta emenda não pode ser editada porque sua etapa é "{form.status}". A edição é permitida apenas enquanto a etapa for "Recebido" ou "Iniciado".
        </div>
      )}

      {/* Formulário */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Dados Básicos */}
        <section className={`${panelClass} p-5`}>
          <h2 className="mb-4 text-base font-semibold text-slate-950 dark:text-slate-50">Dados básicos</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Parlamentar */}
            <div>
              <label className={labelClass}>
                Parlamentar * {isVereador && <span className={`text-xs ${accentTextClass}`}>(Pre-selecionado)</span>}
              </label>
              <div className="relative" ref={councilorRef}>
                <input
                  type="text"
                  placeholder="Buscar parlamentar..."
                  value={councilorSearch}
                  onChange={(e) => { setCouncilorSearch(e.target.value); setCouncilorOpen(true); }}
                  onFocus={() => setCouncilorOpen(true)}
                  className={`${inputClass} mb-2`}
                  disabled={!canEdit || isVereador}
                  aria-autocomplete="list"
                  aria-expanded={councilorOpen}
                />
                {councilorOpen && canEdit && !isVereador && (
                  <ul className={dropdownClass}>
                    {filteredCouncilors.length === 0 ? (
                      <li className="px-3 py-2 text-slate-500">Nenhum parlamentar encontrado</li>
                    ) : (
                      filteredCouncilors.map((c) => (
                        <li
                          key={c.councilorId}
                          onMouseDown={(e) => { e.preventDefault(); }}
                          onClick={() => {
                            handleChange('councilorId', c.councilorId);
                            setCouncilorSearch(c.fullName || '');
                            setCouncilorOpen(false);
                          }}
                          className={dropdownItemClass}
                        >
                          {c.fullName} {c.politicalParty ? `(${c.politicalParty})` : ''}
                        </li>
                      ))
                    )}
                  </ul>
                )}
              </div>
            </div>

            {form.councilorId && (
              <ReservaFuncionalidadesPanel data={reservaFuncionalidades} loading={loadingReserva} />
            )}

            {/* Instituição */}
            <div>
              <label className={labelClass}>
                Instituição * {isInstituicaoLocked && <span className={`text-xs ${accentTextClass}`}>(Pré-selecionada)</span>}
              </label>
              {/* Combobox: single input that opens a dropdown with filtered institutions */}
              {isInstituicaoLocked ? (
                <input
                  type="text"
                  value={institutions.find(i => i.institutionId === form.institutionId)?.razaoSocial || ''}
                  className={`${inputClass} mb-2`}
                  disabled
                />
              ) : (
                <div className="relative" ref={institutionRef}>
                  <input
                    type="text"
                    placeholder="Buscar instituição..."
                    value={institutionSearch}
                    onChange={(e) => { setInstitutionSearch(e.target.value); setInstitutionOpen(true); }}
                    onFocus={() => setInstitutionOpen(true)}
                    className={`${inputClass} mb-2`}
                    disabled={!canEdit}
                    aria-autocomplete="list"
                    aria-expanded={institutionOpen}
                  />
                  {/* Dropdown */}
                  {institutionOpen && canEdit && (
                    <ul className={dropdownClass}>
                      {filteredInstitutions.length === 0 ? (
                        <li className="px-3 py-2 text-slate-500">Nenhuma instituição encontrada</li>
                      ) : (
                        filteredInstitutions.map((inst) => (
                          <li
                            key={inst.institutionId}
                            onMouseDown={(e) => { e.preventDefault(); /* prevent blur before click */ }}
                            onClick={() => {
                              handleChange('institutionId', inst.institutionId);
                              setInstitutionSearch(inst.razaoSocial || (inst as any).name || '');
                              setInstitutionOpen(false);
                            }}
                            className={dropdownItemClass}
                          >
                            {inst.razaoSocial}
                          </li>
                        ))
                      )}
                    </ul>
                  )}
                </div>
              )}
              {isInstituicaoLocked && (
                <p className={`${helperTextClass} ${accentTextClass}`}>
                  Instituição vinculada automaticamente ao criar emenda pelo dashboard
                </p>
              )}
            </div>

            {!autoGenerateOfficialCode && (
              <div>
                <label className={labelClass}>
                  Código Oficial *
                </label>
                <input
                  type="text"
                  required
                  value={form.officialCode}
                  onChange={(e) => handleChange('officialCode', e.target.value)}
                  className={inputClass}
                  placeholder="Ex: 004-132-2025"
                  disabled={!canEdit}
                />
              </div>
            )}

            {/* Data */}
            <div>
              <label className={labelClass}>
                Data *
              </label>
              <input
                type="date"
                required
                value={form.date}
                onChange={(e) => handleChange('date', e.target.value)}
                className={inputClass}
                disabled={!canEdit}
              />
            </div>

            {/* Valor */}
            <div>
              <label className={labelClass}>
                Valor *
              </label>
              <input
                type="text"
                required
                value={form.value}
                onChange={handleValueChange}
                onBlur={(e) => {
                  const num = parseCurrency(e.target.value);
                  handleChange('value', formatCurrency(num));
                }}
                className={inputClass}
                placeholder="R$ 0,00"
                disabled={!canEdit}
              />
            </div>

            {/* Tipo de Emenda */}
            <div>
              <label className={labelClass}>
                Tipo de Emenda *
              </label>
              <select
                required
                value={form.classification}
                onChange={(e) => handleChange('classification', e.target.value)}
                className={inputClass}
                disabled={!canEdit}
              >
                <option value="">Selecione o tipo</option>
                {tiposEmenda.map((t) => (
                  <option key={t.codigo} value={t.codigo}>
                    {t.nome}
                  </option>
                ))}
              </select>
            </div>

            {/* New: Classificação (Investimento/Custeio) */}
            <div>
              <label className={labelClass}>
                Classificação
              </label>
              <select
                value={form.classificacao ?? ''}
                onChange={(e) => handleChange('classificacao', e.target.value)}
                className={inputClass}
                disabled={!canEdit}
              >
                <option value="">Selecione a classificação</option>
                <option value="2">Investimento</option>
                <option value="1">Custeio</option>
              </select>
            </div>

            {/* Exercício: só na criação; o ano de uma emenda já cadastrada não muda por aqui */}
            <div>
              <label className={labelClass}>
                Exercício *
              </label>
              <select
                value={form.exercicio ?? ''}
                onChange={e => handleExercicioChange(Number(e.target.value))}
                className={inputClass}
                disabled={!canEdit || isEditMode}
              >
                {exercicioOptions.map(ano => (
                  <option key={ano} value={ano}>{ano}</option>
                ))}
              </select>
              <p className={helperTextClass}>
                Define o limite do parlamentar e as dotações orçamentárias disponíveis.
              </p>
            </div>

            {/* Função / Área de Atuação */}
            <div>
              <label className={labelClass}>
                Função / Área de Atuação
              </label>
              <select
                value={form.funcaoCodigo ?? ''}
                onChange={e => handleChange('funcaoCodigo', e.target.value)}
                className={inputClass}
                disabled={!canEdit}
              >
                <option value="">Selecione a função / área de atuação...</option>
                {funcoesOrcamentarias.map(f => (
                  <option key={f.codigo} value={f.codigo}>{f.descricao}</option>
                ))}
              </select>
            </div>

            {/* Esfera */}
            <div>
              <label className={labelClass}>
                Esfera
              </label>
              <select
                value={form.esfera}
                onChange={(e) => handleChange('esfera', e.target.value)}
                className={inputClass}
                disabled={!canEdit}
              >
                <option value="Municipal">Municipal</option>
                <option value="Estadual">Estadual</option>
                <option value="Federal">Federal</option>
              </select>
            </div>

            {/* Forma de Execução */}
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                Forma de Execução
              </label>
              <div className="flex gap-6 mt-1">
                {['Direta', 'Indireta'].map(tipo => (
                  <label key={tipo} className="flex cursor-pointer items-center gap-2">
                    <input
                      type="radio"
                      name="tipoTransferencia"
                      value={tipo}
                      checked={form.tipoTransferencia === tipo}
                      onChange={() => handleChange('tipoTransferencia', tipo)}
                      className="accent-blue-600"
                      disabled={!canEdit}
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-200">{tipo}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Dotações Orçamentárias — múltiplas */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Dotações Orçamentárias</label>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => handleChange('dotacoes', [...(form.dotacoes || []), { dotacaoOrcamentariaId: undefined, dotacaoOrcamentariaTexto: '' }])}
                  className="text-xs px-2 py-1 bg-blue-50 border border-blue-200 text-blue-700 rounded hover:bg-blue-100"
                >
                  + Adicionar dotação
                </button>
              )}
            </div>
            <p className="mb-2 text-xs text-gray-500">
              Mostrando dotações de {form.exercicio ?? 'todos os exercícios'}
              {form.funcaoCodigo
                ? ` da área ${funcoesOrcamentarias.find(f => f.codigo === form.funcaoCodigo)?.descricao ?? form.funcaoCodigo}`
                : ' de todas as áreas (escolha a função / área de atuação para filtrar)'}.
              {dotacoesAll.length === 0 && ' Nenhuma dotação cadastrada para esse ano e área.'}
            </p>
            {(form.dotacoes || []).length === 0 && (
              <p className="text-xs text-gray-400 italic">Nenhuma dotação vinculada.</p>
            )}
            {(form.dotacoes || []).map((item, idx) => (
              <div key={idx} className="flex items-start gap-2 mb-2">
                <div className="flex-1">
                  <DotacaoOrcamentariaCombobox
                    options={dotacoesAll}
                    value={item.dotacaoOrcamentariaId}
                    valueText={item.dotacaoOrcamentariaTexto || ''}
                    onChange={(id, text) => {
                      const next = [...(form.dotacoes || [])];
                      next[idx] = { dotacaoOrcamentariaId: id, dotacaoOrcamentariaTexto: text };
                      handleChange('dotacoes', next);
                    }}
                    disabled={!canEdit}
                  />
                </div>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => {
                      const next = (form.dotacoes || []).filter((_, i) => i !== idx);
                      handleChange('dotacoes', next);
                    }}
                    className="mt-6 text-red-400 hover:text-red-600 text-xs px-2 py-1 border border-red-200 rounded"
                    title="Remover dotação"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Descrição */}
          <div className="mt-4">
            <label className={labelClass}>
              Descrição *
            </label>
            <textarea
              required
              value={form.description}
              onChange={(e) => handleChange('description', toUpperText(e.target.value))}
              className={inputClass}
              rows={3}
              maxLength={5000}
              placeholder="Descrição da emenda..."
              disabled={!canEdit}
            />
            <p className={helperTextClass}>{form.description.length}/5000 caracteres</p>
          </div>
        </section>

        {/* Detalhamento */}
        <section className={`${panelClass} p-5`}>
          <h2 className="mb-4 text-base font-semibold text-slate-950 dark:text-slate-50">Detalhamento</h2>

          <div className="space-y-4">
            {/* Objeto Detalhado */}
            <div>
              <label className={labelClass}>
                Objeto Detalhado
              </label>
              <textarea
                value={form.objectDetail}
                onChange={(e) => handleChange('objectDetail', toUpperText(e.target.value))}
                className={inputClass}
                rows={4}
                placeholder="Descrição detalhada do objeto da emenda..."
                disabled={!canEdit}
              />
            </div>

            {/* Justificativa */}
            <div>
              <label className={labelClass}>
                Justificativa
                <span className={`ml-2 text-xs ${helperTextClass}`}>(Opcional - mínimo 20 caracteres se preenchida)</span>
              </label>
              <textarea
                value={form.justificativa}
                onChange={(e) => handleChange('justificativa', toUpperText(e.target.value))}
                className={inputClass}
                rows={3}
                minLength={20}
                maxLength={250}
                placeholder="Justificativa da emenda... (mínimo 20 caracteres)"
                disabled={!canEdit}
              />
              {form.justificativa && (
                <p className={`text-xs mt-1 ${
                  form.justificativa.length < 20 ? 'text-red-600' : 'text-slate-500 dark:text-slate-400'
                }`}>
                  {form.justificativa.length}/250 caracteres
                  {form.justificativa.length < 20 && ' (mínimo 20)'}
                </p>
              )}
            </div>

            {/* Convênio */}
            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.existeConvenio}
                  onChange={(e) => handleChange('existeConvenio', e.target.checked)}
                  className="rounded border-slate-300 text-blue-700 focus:ring-blue-500 dark:border-slate-700"
                  disabled={!canEdit}
                />
                <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Existe Convênio?</span>
              </label>
            </div>

            {form.existeConvenio && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
                <div>
                  <label className={labelClass}>
                    Número do Convênio
                  </label>
                  <input
                    type="text"
                    value={form.numeroConvenio}
                    onChange={(e) => handleChange('numeroConvenio', toUpperText(e.target.value))}
                    className={inputClass}
                    disabled={!canEdit}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    Ano do Convênio
                  </label>
                  <input
                    type="number"
                    value={form.anoConvenio || ''}
                    onChange={(e) => handleChange('anoConvenio', e.target.value ? parseInt(e.target.value) : undefined)}
                    className={inputClass}
                    min={2000}
                    max={new Date().getFullYear() + 5}
                    disabled={!canEdit}
                  />
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Botões */}
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => navigate('/dashboard/emendas')}
            className={secondaryButtonClass}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving || !canEdit}
            className={primaryButtonClass}
          >
            {saving ? 'Salvando...' : (isEditMode ? 'Salvar alterações' : 'Criar emenda')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CadastroEmendaPage;
